import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Bone, ChevronDown, ClipboardCheck, Eraser, ShoppingBag, User, X } from "lucide-react";
import { Input } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { MultiSelect, type MultiSelectOption } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { buildActiveChips, type ChipLabels, type ChipLookups } from "./active-chips";
import { isSalesExplorerFilterGroupVisible } from "./filter-groups";
import type { FinancialStatusFilter, ItemCountOperator, SalesExplorerFilters } from "./types";

export interface SalesExplorerOptions {
  appointmentTypes: { id: string; name: string }[];
  appointmentStatuses: { value: string; label: string }[];
  professionals: { id: string; name: string }[];
  items: { id: string; name: string }[];
  categories: { id: string; name: string }[];
  species: { id: string; name: string }[];
  breeds: { id: string; name: string }[];
  sizeCategories: { id: string; name: string }[];
}

interface Props {
  filters: SalesExplorerFilters;
  onChange: (next: SalesExplorerFilters) => void;
  options: SalesExplorerOptions;
}

type GroupId = "appointment" | "items" | "pet" | "owner";

const toOptions = (list: { id: string; name: string }[]): MultiSelectOption[] =>
  list.map((entry) => ({ label: entry.name, value: entry.id }));

/**
 * Altura e raio dos controles vêm do design (48px, --radius-md = 12px).
 *
 * O `rounded-md` no MultiSelect não é decorativo: seu gatilho é um
 * `.akuna-button`, que por padrão usa `--radius-pill` e deixava o autocomplete
 * arredondado como cápsula enquanto os inputs (`.akuna-control`) ficavam com
 * 12px. Medido no browser: `.akuna-button` sozinho dá 9999px, com `rounded-md`
 * dá 12px — igual ao `.akuna-control`.
 */
const CONTROL = "h-12 rounded-md shadow-akuna-xs";
const MULTI_CONTROL = "h-12 rounded-md px-3.5 shadow-akuna-xs";

function FilterField({
  children,
  help,
  label,
}: {
  children: ReactNode;
  help?: string;
  label: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-[7px]">
      <Label className="text-[13px] font-semibold text-muted-foreground">{label}</Label>
      {children}
      {help ? <small className="text-xs leading-[1.35] text-color-light">{help}</small> : null}
    </div>
  );
}

export function SalesExplorerFiltersPanel({ filters, onChange, options }: Props) {
  const { t } = useTranslation("reports");
  const [openGroup, setOpenGroup] = useState<GroupId | null>("appointment");

  // Qualquer mudança de filtro volta para a primeira página: manter a página
  // atual mostraria "nenhum resultado" num recorte que tem resultado.
  const update = (partial: Partial<SalesExplorerFilters>) =>
    onChange({ ...filters, ...partial, page: 1 });

  const chipLookups: ChipLookups = {
    appointmentStatuses: options.appointmentStatuses.map((entry) => ({ id: entry.value, name: entry.label })),
    appointmentTypes: options.appointmentTypes,
    breeds: options.breeds,
    categories: options.categories,
    items: options.items,
    professionals: options.professionals,
    sizeCategories: options.sizeCategories,
    species: options.species,
  };

  const chipLabels: ChipLabels = {
    appointmentStatus: t("salesExplorerAppointmentStatus"),
    appointmentType: t("salesExplorerAppointmentType"),
    breed: t("salesExplorerBreed"),
    category: t("salesExplorerCategory"),
    financialStatus: t("salesExplorerFinancialStatus"),
    item: t("salesExplorerItem"),
    itemCount: t("salesExplorerItemCount"),
    itemType: t("salesExplorerItemType"),
    ownerCity: t("salesExplorerOwnerCity"),
    ownerNeighborhood: t("salesExplorerOwnerNeighborhood"),
    ownerSearch: t("salesExplorerOwnerSearch"),
    professional: t("salesExplorerProfessional"),
    size: t("salesExplorerSize"),
    species: t("salesExplorerSpecies"),
    weight: t("salesExplorerWeight"),
  };

  const chips = buildActiveChips(filters, chipLookups, chipLabels);
  const itemCount = filters.itemCount;

  const groupCounts: Record<GroupId, number> = {
    appointment:
      filters.appointmentTypeIds.length +
      filters.appointmentStatuses.length +
      filters.professionalIds.length +
      (filters.financialStatus !== "all" ? 1 : 0),
    items:
      filters.itemIds.length +
      filters.itemTypes.length +
      filters.categoryIds.length +
      (filters.itemCount ? 1 : 0),
    owner:
      (filters.ownerSearch.trim() ? 1 : 0) +
      (filters.ownerCity.trim() ? 1 : 0) +
      (filters.ownerNeighborhood.trim() ? 1 : 0),
    pet:
      filters.speciesIds.length +
      filters.breedIds.length +
      filters.sizeCategoryIds.length +
      (filters.weightMin !== null || filters.weightMax !== null ? 1 : 0),
  };

  const clearAll = () =>
    onChange({
      ...filters,
      appointmentTypeIds: [],
      appointmentStatuses: [],
      professionalIds: [],
      financialStatus: "all",
      itemIds: [],
      itemTypes: [],
      categoryIds: [],
      itemCount: null,
      speciesIds: [],
      breedIds: [],
      sizeCategoryIds: [],
      weightMin: null,
      weightMax: null,
      ownerSearch: "",
      ownerCity: "",
      ownerNeighborhood: "",
      page: 1,
    });

  const groups: { desc: string; fields: ReactNode; icon: ReactNode; id: GroupId; name: string }[] = [
    {
      desc: t("salesExplorerGroupAppointmentDesc"),
      icon: <ClipboardCheck size={19} />,
      id: "appointment",
      name: t("salesExplorerFilterAppointment"),
      fields: (
        <>
          <FilterField label={t("salesExplorerAppointmentType")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ appointmentTypeIds: values })}
              options={toOptions(options.appointmentTypes)}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.appointmentTypeIds}
            />
          </FilterField>
          <FilterField label={t("salesExplorerAppointmentStatus")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ appointmentStatuses: values })}
              options={options.appointmentStatuses.map((entry) => ({ label: entry.label, value: entry.value }))}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.appointmentStatuses}
            />
          </FilterField>
          <FilterField label={t("salesExplorerProfessional")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ professionalIds: values })}
              options={toOptions(options.professionals)}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.professionalIds}
            />
          </FilterField>
          <FilterField label={t("salesExplorerFinancialStatus")}>
            <Select
              onValueChange={(value) => update({ financialStatus: value as FinancialStatusFilter })}
              value={filters.financialStatus}
            >
              <SelectTrigger className={CONTROL}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("salesExplorerFinancialAll")}</SelectItem>
                <SelectItem value="paid">{t("salesExplorerFinancialPaid")}</SelectItem>
                <SelectItem value="open">{t("salesExplorerFinancialOpen")}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </>
      ),
    },
    {
      desc: t("salesExplorerGroupItemsDesc"),
      icon: <ShoppingBag size={19} />,
      id: "items",
      name: t("salesExplorerFilterItems"),
      fields: (
        <>
          <FilterField label={t("salesExplorerItem")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ itemIds: values })}
              options={toOptions(options.items)}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.itemIds}
            />
          </FilterField>
          <FilterField label={t("salesExplorerItemType")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ itemTypes: values as SalesExplorerFilters["itemTypes"] })}
              options={[
                { label: t("salesExplorerItemTypeProduct"), value: "PRODUTO" },
                { label: t("salesExplorerItemTypeService"), value: "SERVICO" },
                { label: t("salesExplorerItemTypePackage"), value: "PACOTE" },
              ]}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.itemTypes}
            />
          </FilterField>
          <FilterField label={t("salesExplorerCategory")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ categoryIds: values })}
              options={toOptions(options.categories)}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.categoryIds}
            />
          </FilterField>
          <FilterField label={t("salesExplorerItemCount")}>
            <div className="flex gap-2">
              <Select
                onValueChange={(value) =>
                  update({
                    itemCount: {
                      operator: value as ItemCountOperator,
                      scope: "all",
                      value: itemCount?.value ?? 0,
                    },
                  })
                }
                value={itemCount?.operator ?? "gte"}
              >
                <SelectTrigger aria-label={t("salesExplorerItemCountOperator")} className={cn(CONTROL, "flex-1")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gte">{t("salesExplorerItemCountGte")}</SelectItem>
                  <SelectItem value="eq">{t("salesExplorerItemCountEq")}</SelectItem>
                  <SelectItem value="lte">{t("salesExplorerItemCountLte")}</SelectItem>
                </SelectContent>
              </Select>
              <Input
                aria-label={t("salesExplorerItemCountValue")}
                className={cn(CONTROL, "w-[76px] text-center")}
                min={0}
                onChange={(event) => {
                  const raw = event.target.value;
                  update({
                    itemCount:
                      raw === ""
                        ? null
                        : { operator: itemCount?.operator ?? "gte", scope: "all", value: Number(raw) },
                  });
                }}
                placeholder="0"
                type="number"
                value={itemCount?.value ?? ""}
              />
            </div>
          </FilterField>
        </>
      ),
    },
    {
      desc: t("salesExplorerGroupPetDesc"),
      icon: <Bone size={19} />,
      id: "pet",
      name: t("salesExplorerFilterPet"),
      fields: (
        <>
          <FilterField label={t("salesExplorerSpecies")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ speciesIds: values })}
              options={toOptions(options.species)}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.speciesIds}
            />
          </FilterField>
          <FilterField label={t("salesExplorerBreed")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ breedIds: values })}
              options={toOptions(options.breeds)}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.breedIds}
            />
          </FilterField>
          <FilterField label={t("salesExplorerSize")}>
            <MultiSelect
              className={MULTI_CONTROL}
              onChange={(values) => update({ sizeCategoryIds: values })}
              options={toOptions(options.sizeCategories)}
              placeholder={t("salesExplorerAllPlaceholder")}
              selected={filters.sizeCategoryIds}
            />
          </FilterField>
          <FilterField label={t("salesExplorerWeight")}>
            <div className="flex items-center gap-2.5">
              <Input
                aria-label={t("salesExplorerWeightMin")}
                className={cn(CONTROL, "flex-1")}
                min={0}
                onChange={(event) =>
                  update({ weightMin: event.target.value === "" ? null : Number(event.target.value) })
                }
                placeholder={t("salesExplorerWeightMin")}
                step="0.1"
                type="number"
                value={filters.weightMin ?? ""}
              />
              <span className="font-semibold text-color-light">—</span>
              <Input
                aria-label={t("salesExplorerWeightMax")}
                className={cn(CONTROL, "flex-1")}
                min={0}
                onChange={(event) =>
                  update({ weightMax: event.target.value === "" ? null : Number(event.target.value) })
                }
                placeholder={t("salesExplorerWeightMax")}
                step="0.1"
                type="number"
                value={filters.weightMax ?? ""}
              />
            </div>
          </FilterField>
        </>
      ),
    },
    {
      desc: t("salesExplorerGroupOwnerDesc"),
      icon: <User size={19} />,
      id: "owner",
      name: t("salesExplorerFilterOwner"),
      fields: (
        <>
          <FilterField label={t("salesExplorerOwnerCity")}>
            <Input
              className={CONTROL}
              onChange={(event) => update({ ownerCity: event.target.value })}
              placeholder={t("salesExplorerOwnerCityPlaceholder")}
              value={filters.ownerCity}
            />
          </FilterField>
          <FilterField label={t("salesExplorerOwnerNeighborhood")}>
            <Input
              className={CONTROL}
              onChange={(event) => update({ ownerNeighborhood: event.target.value })}
              placeholder={t("salesExplorerOwnerNeighborhoodPlaceholder")}
              value={filters.ownerNeighborhood}
            />
          </FilterField>
        </>
      ),
    },
  ];

  return (
    <div className="overflow-hidden rounded-card border bg-card shadow-akuna-sm">
      {/* resumo */}
      <div className="flex flex-wrap items-center gap-2.5 px-[18px] pb-[15px] pt-4">
        <span className="font-display text-[15px] font-bold">{t("salesExplorerFiltersTitle")}</span>
        {chips.length > 0 ? (
          <span className="inline-flex h-[22px] items-center rounded-full bg-akuna-orange px-2.5 text-xs font-bold text-cream-100">
            {t("salesExplorerActiveCount", { count: chips.length })}
          </span>
        ) : null}
        {chips.length > 0 ? (
          <button
            className="ml-auto inline-flex items-center gap-[7px] px-1 py-1.5 text-sm font-semibold text-akuna-orange transition-colors hover:text-orange-600"
            onClick={clearAll}
            type="button"
          >
            <Eraser size={16} />
            {t("salesExplorerClearAll")}
          </button>
        ) : null}

        {chips.length > 0 ? (
          <div className="mt-0.5 flex basis-full flex-wrap gap-2">
            {chips.map((chip) => (
              <span
                className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-orange-50 py-1.5 pl-3 pr-2.5 text-[13px]"
                key={chip.id}
              >
                <span className="text-orange-600">{chip.label}:</span>
                <strong className="text-orange-700">{chip.value}</strong>
                <button
                  aria-label={t("salesExplorerRemoveFilter")}
                  className="grid h-[18px] w-[18px] place-items-center rounded-full text-orange-500 transition-colors hover:bg-orange-100 hover:text-orange-700"
                  onClick={() => onChange(chip.remove(filters))}
                  type="button"
                >
                  <X size={13} />
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {/* sanfona */}
      {groups.filter((group) => isSalesExplorerFilterGroupVisible(group.id)).map((group) => {
        const count = groupCounts[group.id];
        const isOpen = openGroup === group.id;

        return (
          <div className="border-t" key={group.id}>
            <button
              aria-expanded={isOpen}
              className="flex w-full items-center gap-3.5 px-[18px] py-[15px] text-left transition-colors hover:bg-muted/30"
              onClick={() => setOpenGroup((current) => (current === group.id ? null : group.id))}
              type="button"
            >
              <span className="grid h-10 w-10 flex-none place-items-center rounded-md bg-orange-50 text-akuna-orange">
                {group.icon}
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-semibold text-foreground">{group.name}</span>
                <span className="block text-[13px] text-muted-foreground">{group.desc}</span>
              </span>
              <span className="ml-auto flex items-center gap-3">
                {count > 0 ? (
                  <span className="inline-flex h-6 items-center rounded-full bg-akuna-orange px-[11px] text-xs font-bold text-cream-100">
                    {t("salesExplorerGroupCount", { count })}
                  </span>
                ) : null}
                <ChevronDown
                  className={cn("text-color-light transition-transform duration-200", isOpen && "rotate-180")}
                  size={20}
                />
              </span>
            </button>

            {isOpen ? (
              <div className="grid grid-cols-1 gap-x-[22px] gap-y-5 px-[18px] pb-6 pt-0.5 sm:grid-cols-2 lg:grid-cols-4">
                {group.fields}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
